import { useEffect, useState } from 'react';
import { getBreakpointIdentifier, getCookie, toTitleCase, variableCheckPromise } from './helpers';
import { EventName, postUiLog } from './uilog';

const GATSBY_CORVETTE_HOST = process.env.GATSBY_CORVETTE_HOST;

const GATSBY_HYDRATION_FALLBACK_WAIT = 2500;
const AB_TEST_CAMPAIGN_WAIT_TIMEOUT_MS = 6000;

let gatsbyHydrationCompleteDispatched = false;
let gatsbyHydrationCompleteFallbackTimer: number | null = null;
let gatsbyHydrationManagedByHook = false;
let hydrationFallbackUiLogPosted = false;
let alertsHydrationOwnerActive = false;

const postReturnUserAlertUiLog = (postData: Record<string, any>): void => {
  postUiLog(EventName.alerts, postData);

  // postUiLog mutates window.ECS.tms.data.response. Clear it after the
  // return-user-alert uilog is posted so response_category does not leak
  // into later events such as event_pageChange.
  if (typeof window !== 'undefined' && window.ECS?.tms?.data) {
    delete window.ECS.tms.data.response;
  }
};

const postHydrationFallbackUiLog = (): void => {
  if (hydrationFallbackUiLogPosted) {
    return;
  }

  hydrationFallbackUiLogPosted = true;

  postReturnUserAlertUiLog({
    placement: 'nav-public-header-alerts',
    category: 'new-user-fallback'
  });
};

const dispatchGatsbyHydrationComplete = () => {
  if (
    typeof window === 'undefined' ||
    gatsbyHydrationCompleteDispatched ||
    window.gatsbyHydrationComplete
  ) {
    return;
  }

  gatsbyHydrationCompleteDispatched = true;

  if (gatsbyHydrationCompleteFallbackTimer !== null) {
    window.clearTimeout(gatsbyHydrationCompleteFallbackTimer);
    gatsbyHydrationCompleteFallbackTimer = null;
  }

  const event = new CustomEvent('gatsbyHydrationComplete', {
    bubbles: true,
    cancelable: false
  });

  window.dispatchEvent(event);
  window.gatsbyHydrationComplete = true;
};

const waitForPendingAbTestCampaign = (): Promise<void> => {
  if (typeof window === 'undefined' || window.ecsTstCampaignStatus !== 'pending') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    let timer: number | null = null;

    const cleanup = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }

      window.removeEventListener('ecsExperimentAttributionApplied', onApplied);
    };

    const finish = () => {
      cleanup();
      resolve();
    };

    const onApplied = () => {
      if (
        window.ecsTstCampaignStatus === 'applied' ||
        window.ecsTstCampaignStatus === 'skipped' ||
        window.ecsTstCampaignStatus !== 'pending'
      ) {
        finish();
      }
    };

    timer = window.setTimeout(finish, AB_TEST_CAMPAIGN_WAIT_TIMEOUT_MS);

    window.addEventListener('ecsExperimentAttributionApplied', onApplied, { once: true });

    // Guard against a race where attribution resolves between the initial
    // pending-status check and listener registration.
    onApplied();
  });
};

const dispatchGatsbyHydrationCompleteAfterCampaign = async () => {
  await waitForPendingAbTestCampaign();
  dispatchGatsbyHydrationComplete();
};

const queueGatsbyHydrationComplete = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.setTimeout(() => {
    void dispatchGatsbyHydrationCompleteAfterCampaign();
  }, 0);
};

export const scheduleGatsbyHydrationCompleteFallback = (delay = GATSBY_HYDRATION_FALLBACK_WAIT) => {
  if (
    typeof window === 'undefined' ||
    gatsbyHydrationCompleteDispatched ||
    window.gatsbyHydrationComplete
  ) {
    return;
  }

  if (gatsbyHydrationCompleteFallbackTimer !== null) {
    window.clearTimeout(gatsbyHydrationCompleteFallbackTimer);
  }

  gatsbyHydrationCompleteFallbackTimer = window.setTimeout(() => {
    gatsbyHydrationCompleteFallbackTimer = null;

    if (
      gatsbyHydrationManagedByHook ||
      gatsbyHydrationCompleteDispatched ||
      window.gatsbyHydrationComplete
    ) {
      return;
    }

    postHydrationFallbackUiLog();
    void dispatchGatsbyHydrationCompleteAfterCampaign();
  }, delay);
};

// Remember to remove this flag once the hardcoded return experiences are stable and working as expected.
// If you need to quickly disable repo-side return-user determination, temporarily set this to false.
// When false, repo-side return-user/token/profile/alerts logic is disabled.
const ENABLE_RETURN_USER_TEMP_FLAG = true;

const isReturnUserEnabled = (): boolean => ENABLE_RETURN_USER_TEMP_FLAG;

interface LimitedToken {
  accessToken: string;
  tokenType: string;
  expiresIn: number; // seconds
  idleExp: number; // seconds
}

interface TokenResponse {
  limitedToken: LimitedToken;
  redirectType: string;
}

interface UserSession {
  token: string;
  savedAt: number;
  idleExp: number;
  marketingId: string;
  userName: string | null;
  alertsCount: number | null;
}

// Shared in-flight requests, this prevents multiple API requests if multiple components request at the same time token, user name or alerts number
let ongoingTokenRequest: Promise<string | null> | null = null;
let ongoingUserRequest: Promise<string | null> | null = null;
let ongoingAlertsRequest: Promise<number | null> | null = null;

function isTokenValid(session: UserSession) {
  const deltaMs = 1 * 60 * 1000; // 1 minute in milliseconds
  const now = Date.now();
  const expiryTime = session.savedAt + session.idleExp * 1000;
  return now < expiryTime - deltaMs;
}

const SESSION_KEY = 'userSession';

// --- MARKETING ID ---
export function getMarketingId(): string | null {
  if (typeof window === 'undefined') return null;

  // 1. Check URL for ?mkid=
  const urlParams = new URLSearchParams(window.location.search);
  const mkidParam = urlParams.get('mkid');
  if (mkidParam && isValidMarketingId(mkidParam)) {
    return mkidParam;
  }

  // 2. Direct cookie: marketingid
  const marketingIdCookie = getCookie('marketingid');
  if (marketingIdCookie && isValidMarketingId(marketingIdCookie)) {
    return marketingIdCookie;
  }

  // 3. tmsData cookie
  const tmsDataCookie = getCookie('tmsData');
  const midFromTms = getMarketingIdFromTmsData(tmsDataCookie);
  if (midFromTms) {
    return midFromTms;
  }
  return null;
}

function getMarketingIdFromTmsData(tmsDataCookie: string | null): string | null {
  if (!tmsDataCookie) return null;

  try {
    const parsed = JSON.parse(tmsDataCookie);
    const mid = parsed?.member?.marketingid;
    if (mid && isValidMarketingId(mid)) return mid;
    return null;
  } catch (err) {
    console.warn('Failed to parse tmsData cookie, ignoring it', err);
    return null; // safely ignore parse errors
  }
}

export function isValidMarketingId(id: string): boolean {
  // Standard GUID regex
  const guidRegex = /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/;

  if (!guidRegex.test(id)) return false;

  // Disallow all zeros: 00000000-0000-0000-0000-000000000000
  if (/^0{8}-(0{4}-){3}0{12}$/.test(id)) return false;

  return true;
}

// --- TOKEN ---
export async function getToken({
  mid = null,
  force = false
}: {
  mid?: string | null;
  force?: boolean;
} = {}): Promise<string | null> {
  let marketingId: string | null | undefined = mid;
  // Load session from storage
  let session: UserSession | null = null;
  const sessionStr = sessionStorage.getItem(SESSION_KEY);
  if (sessionStr) {
    try {
      session = JSON.parse(sessionStr);
    } catch (err) {
      sessionStorage.removeItem(SESSION_KEY);
      session = null;
    }
  }
  if (session && (!mid || session.marketingId === mid) && !force && isTokenValid(session)) {
    return session.token;
  }

  if (ongoingTokenRequest) return ongoingTokenRequest;

  ongoingTokenRequest = (async () => {
    if (!marketingId) {
      marketingId = getMarketingId();
    }

    if (!marketingId) {
      ongoingTokenRequest = null;
      return null;
    }

    try {
      const res = await fetchTokenFromApi(marketingId);

      session = {
        token: res.limitedToken.accessToken,
        savedAt: Date.now(),
        idleExp: res.limitedToken.idleExp,
        marketingId,
        userName: null,
        alertsCount: null
      };

      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));

      return session.token;
    } catch (err) {
      console.error('Failed to fetch token', err);

      postReturnUserAlertUiLog({
        placement: 'nav-public-header-alerts',
        category: 'alert-NaN'
      });

      return null;
    } finally {
      ongoingTokenRequest = null;
    }
  })();

  return ongoingTokenRequest;
}

async function fetchTokenFromApi(marketingId: string): Promise<TokenResponse> {
  const payload = {
    clientId: 'experian',
    authorizationLevel: 'AL_100',
    marketingId
  };

  const sessionId = await variableCheckPromise(() => window?.ECS?.tms?.data?.sessionid);

  const response = await fetch(`${GATSBY_CORVETTE_HOST}/api/securelogin/limited/access/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      'x-session-id': sessionId || ''
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) throw new Error('Token request failed');
  return response.json();
}

// --- USER NAME ---
type UIProfileAPIResponse = {
  customer: { name: { first: string } };
};

export async function getUserName(token: string): Promise<string | null> {
  // Load session from storage
  let session: UserSession | null = null;
  const sessionStr = sessionStorage.getItem(SESSION_KEY);
  if (sessionStr) {
    try {
      session = JSON.parse(sessionStr);
    } catch (err) {
      sessionStorage.removeItem(SESSION_KEY);
      session = null;
    }
  }

  if (session?.userName) {
    return toTitleCase(session.userName);
  }

  // If a request is already in flight, reuse it
  if (ongoingUserRequest) return ongoingUserRequest;

  ongoingUserRequest = (async () => {
    try {
      const response = await fetch(`${GATSBY_CORVETTE_HOST}/api/uiprofile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${token}`
        }
      });

      // Silently return null if request fails
      if (!response.ok) {
        return null;
      }

      const data: UIProfileAPIResponse = await response.json();
      const name = toTitleCase(data.customer?.name?.first);

      if (session) {
        session.userName = name;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      }

      return name;
    } catch (err) {
      console.error('Failed to fetch user name', err);
      return null;
    } finally {
      ongoingUserRequest = null;
    }
  })();

  return ongoingUserRequest;
}

// --- ALERTS ---
type AlertsAPIResponse = {
  data?: {
    eCommerceUserInfo?: {
      customRetargeting?: { unreadAlertCount?: number | null };
    };
  };
};

export async function getAlertsCount(token: string): Promise<number | null> {
  // Load session from storage
  let session: UserSession | null = null;
  const sessionStr = sessionStorage.getItem(SESSION_KEY);
  if (sessionStr) {
    try {
      session = JSON.parse(sessionStr);
    } catch (err) {
      sessionStorage.removeItem(SESSION_KEY);
      session = null;
    }
  }

  // If token matches and alerts are cached, return cached value
  if (session?.alertsCount != null) {
    return session.alertsCount;
  }

  // Always fetch alerts if token is new or cache missing
  if (ongoingAlertsRequest) return ongoingAlertsRequest;

  ongoingAlertsRequest = (async () => {
    try {
      const query = `
        query customer {
          eCommerceUserInfo {
            customRetargeting {
              unreadAlertCount
            }
          }
        }
      `;

      const response = await fetch(`${GATSBY_CORVETTE_HOST}/api/router/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ query, operationName: 'customer' })
      });

      // Silently return null if request fails
      if (!response.ok) return null;

      const data: AlertsAPIResponse = await response.json();
      const unreadAlertCount = data?.data?.eCommerceUserInfo?.customRetargeting?.unreadAlertCount;
      const count = typeof unreadAlertCount === 'number' ? unreadAlertCount : null;

      // Update session storage
      if (session) {
        session.alertsCount = count; // update alerts
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      }

      return count;
    } catch (err) {
      console.error('Failed to fetch alerts', err);
      postReturnUserAlertUiLog({
        placement: 'nav-public-header-alerts',
        category: 'alert-NaN'
      });
      return null;
    } finally {
      ongoingAlertsRequest = null;
    }
  })();

  return ongoingAlertsRequest;
}
// use it on pages with return user experiences

const getQueryParam = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(key);
};

const normalizeParamValue = (value?: string | null): string => (value ?? '').trim().toLowerCase();

export function useReturnUser(blockedCcParams?: string[], includeRefParams?: string[]) {
  const [loading, setLoading] = useState(isReturnUserEnabled());
  const [marketingId, setMarketingId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [isReturnUser, setIsReturnUser] = useState(false);

  useEffect(() => {
    gatsbyHydrationManagedByHook = true;

    if (!isReturnUserEnabled()) {
      setMarketingId(null);
      setUserName(null);
      setIsReturnUser(false);
      setLoading(false);
      return;
    }

    let mounted = true;

    const init = async () => {
      try {
        setLoading(true);

        const cc = normalizeParamValue(getQueryParam('cc'));
        const ref = normalizeParamValue(getQueryParam('ref'));

        const normalizedBlockedCcParams = blockedCcParams?.map(normalizeParamValue);
        const normalizedIncludeRefParams = includeRefParams?.map(normalizeParamValue);
        const hasIncludedRefParam = Boolean(ref && normalizedIncludeRefParams?.includes(ref));

        // Determine page mode
        const isRefOnlyPage = Boolean(normalizedIncludeRefParams && !normalizedBlockedCcParams);

        let isAllowed = true;

        // CC gating (only if page provided CC list)
        if (cc && normalizedBlockedCcParams) {
          isAllowed = !normalizedBlockedCcParams.includes(cc);
        }

        // REF gating (value check)
        if (isAllowed && ref && normalizedIncludeRefParams) {
          isAllowed = hasIncludedRefParam;
        }

        // REF gating (value check)
        if (isAllowed && isRefOnlyPage && !ref) {
          isAllowed = false;
        }

        if (!isAllowed) {
          setIsReturnUser(false);
          setLoading(false);
          return;
        }

        const existingSession = getExistingValidSession();
        const mid = getMarketingId() ?? existingSession?.marketingId ?? null;
        if (!mounted) {
          return;
        }

        if (!mid) {
          setMarketingId(null);
          setUserName(null);

          if (hasIncludedRefParam) {
            setIsReturnUser(true);
          } else {
            setIsReturnUser(false);
          }

          setLoading(false);
          return;
        }

        setMarketingId(mid);

        const sessionToken =
          existingSession && existingSession.marketingId === mid ? existingSession.token : null;
        const token = sessionToken ?? (await getToken({ mid }));

        if (!mounted) {
          return;
        }

        if (!token) {
          setUserName(null);

          if (hasIncludedRefParam) {
            setIsReturnUser(true);
          } else {
            setIsReturnUser(false);
          }

          setLoading(false);
          return;
        }

        // return user confirmed
        setIsReturnUser(true);

        // Keep the loader active until the first name is either available or confirmed unavailable,
        // so return-user page content does not render and then shift when the name arrives.
        const name = await getUserName(token);

        if (!mounted) {
          return;
        }

        setUserName(name);
        setLoading(false);
      } catch (err) {
        console.error('Return user flow failed', err);
        if (mounted) {
          setIsReturnUser(false);
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, [blockedCcParams, includeRefParams]);

  useEffect(() => {
    if (!loading && !alertsHydrationOwnerActive) {
      void dispatchGatsbyHydrationCompleteAfterCampaign();
    }
  }, [loading]);

  return {
    loading,
    marketingId,
    userName,
    isReturnUser
  };
}

function getExistingSessionToken(): string | null {
  try {
    const sessionStr = sessionStorage.getItem(SESSION_KEY);
    if (!sessionStr) return null;

    const session: UserSession = JSON.parse(sessionStr);
    return isTokenValid(session) ? session.token : null;
  } catch {
    return null;
  }
}

function getExistingValidSession(): UserSession | null {
  try {
    const sessionStr = sessionStorage.getItem(SESSION_KEY);
    if (!sessionStr) return null;

    const session: UserSession = JSON.parse(sessionStr);
    return isTokenValid(session) ? session : null;
  } catch {
    return null;
  }
}

const RETURN_USER_ALERT_CAMPAIGN = {
  campaign: 'RUHCTST',
  recipe: 'B1',
  extra: 'HRD01',
  persist: true
};

const RETURN_USER_ALERT_DECIBEL_PREFIX = `${RETURN_USER_ALERT_CAMPAIGN.campaign}-${RETURN_USER_ALERT_CAMPAIGN.recipe}-`;

function isReturnUserAlertsCampaign(
  campaign?: {
    campaign?: string;
    recipe?: string;
    extra?: string;
  } | null
): boolean {
  return (
    campaign?.campaign === RETURN_USER_ALERT_CAMPAIGN.campaign &&
    campaign?.recipe === RETURN_USER_ALERT_CAMPAIGN.recipe
  );
}

function isReturnUserAlertsDecibelValue(value?: unknown): boolean {
  return typeof value === 'string' && value.startsWith(RETURN_USER_ALERT_DECIBEL_PREFIX);
}

function getReturnUserAlertsDecibelRecipe(): string {
  return `${RETURN_USER_ALERT_CAMPAIGN.campaign}-${RETURN_USER_ALERT_CAMPAIGN.recipe}-${getBreakpointIdentifier()}-${RETURN_USER_ALERT_CAMPAIGN.extra}`;
}

function clearAlertsCampaignGlobals() {
  if (typeof window === 'undefined') return;

  if (isReturnUserAlertsCampaign(window.ecsTstCampaign)) {
    delete window.ecsTstCampaign;
  }

  if (isReturnUserAlertsDecibelValue(window.ecsTstDecibelRecipe)) {
    delete window.ecsTstDecibelRecipe;
  }
}

function applyRepoAlertsCampaignGlobalsBeforeHydration(): boolean {
  if (typeof window === 'undefined') return false;

  clearAlertsCampaignGlobals();

  window.ecsTstCampaign = { ...RETURN_USER_ALERT_CAMPAIGN };
  window.ecsTstDecibelRecipe = getReturnUserAlertsDecibelRecipe();

  if (window.opAppend) {
    window.opAppend();
  }

  return true;
}

export function useAlerts(enabled = true, applyAlertsCampaign = true) {
  const [loading, setLoading] = useState(() => enabled && isReturnUserEnabled());
  const [marketingId, setMarketingId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [alertsCount, setAlertsCount] = useState<number | null>(null);
  const [isReturnUser, setIsReturnUser] = useState(false);
  const [showAlertsBell, setShowAlertsBell] = useState(false);
  const shouldOwnHydration = enabled && isReturnUserEnabled();

  // Claim hydration ownership synchronously during render so useReturnUser()
  // cannot dispatch gatsbyHydrationComplete before useAlerts() has decided whether
  // repo-side RUHCTST should own return-user header attribution.
  if (shouldOwnHydration) {
    alertsHydrationOwnerActive = true;
  }

  useEffect(() => {
    let mounted = true;
    let shouldClearRepoCampaignGlobals = false;

    if (shouldOwnHydration) {
      setLoading(true);
    }

    const resetAlertsState = () => {
      setMarketingId(null);
      setUserName(null);
      setIsReturnUser(false);
      setAlertsCount(null);
      setShowAlertsBell(false);
      setLoading(false);
    };

    const initAlerts = async () => {
      gatsbyHydrationManagedByHook = true;

      if (!shouldOwnHydration) {
        if (shouldClearRepoCampaignGlobals) {
          clearAlertsCampaignGlobals();
        }

        resetAlertsState();

        queueGatsbyHydrationComplete();

        return;
      }

      setLoading(true);

      try {
        const mid = getMarketingId();
        if (!mounted) return;

        setMarketingId(mid);

        // NO MID → NO ALERTS
        if (!mid) {
          if (shouldClearRepoCampaignGlobals) {
            clearAlertsCampaignGlobals();
          }

          setUserName(null);
          setAlertsCount(null);
          setShowAlertsBell(false);
          setIsReturnUser(false);
          return;
        }

        // Use existing token when available; otherwise fetch/reuse the shared in-flight token request.
        const existingToken = getExistingSessionToken();
        const token = existingToken ?? (await getToken({ mid }));

        if (!mounted || !token) {
          if (shouldClearRepoCampaignGlobals) {
            clearAlertsCampaignGlobals();
          }

          setUserName(null);
          setAlertsCount(null);
          setShowAlertsBell(false);
          setIsReturnUser(false);
          return;
        }

        // Confirmed return user.
        setIsReturnUser(true);
        setShowAlertsBell(true);

        if (applyAlertsCampaign) {
          shouldClearRepoCampaignGlobals = applyRepoAlertsCampaignGlobalsBeforeHydration();
        } else {
          clearAlertsCampaignGlobals();
        }

        if (!mounted) {
          if (shouldClearRepoCampaignGlobals) {
            clearAlertsCampaignGlobals();
          }

          return;
        }

        // Let the return-user variation render as soon as the user is confirmed
        // and campaign ownership has been determined.
        setLoading(false);

        // Fire the analytics hydration signal as soon as campaign ownership is known.
        // Do not wait for slower, non-critical profile/alert-count requests.
        queueGatsbyHydrationComplete();

        const name = await getUserName(token);
        if (!mounted) return;
        setUserName(name);

        const count = await getAlertsCount(token);

        if (!mounted) return;

        setAlertsCount(count);

        postReturnUserAlertUiLog({
          placement: 'nav-public-header-alerts',
          category: 'alerts-displayed',
          value: count
        });
      } catch (err) {
        if (shouldClearRepoCampaignGlobals) {
          clearAlertsCampaignGlobals();
        }

        console.error('Failed to fetch alerts', err);

        if (mounted) {
          setIsReturnUser(false);
          setShowAlertsBell(false);
        }
      } finally {
        if (mounted) {
          setLoading(false);

          queueGatsbyHydrationComplete();
        }
      }
    };

    initAlerts();

    return () => {
      mounted = false;

      if (shouldOwnHydration) {
        alertsHydrationOwnerActive = false;
      }

      if (shouldClearRepoCampaignGlobals) {
        clearAlertsCampaignGlobals();
      }
    };
  }, [enabled, shouldOwnHydration, applyAlertsCampaign]);

  const showAlertsDot = alertsCount !== null && alertsCount > 0;

  const alertText = !isReturnUser
    ? 'Sign in to see your alert notifications.'
    : userName
      ? showAlertsDot
        ? `${userName}, you have unread alerts to review.`
        : `${userName}, sign in to see your alert notifications.`
      : 'Sign in to see your alert notifications.';

  return {
    loading,
    marketingId,
    userName,
    alertsCount,
    isReturnUser,
    alertText,
    showAlertsBell,
    showAlertsDot
  };
}